<template>
<Panel>
  <div class="">
    <div class="">
      <label class="block text-xs font-bold tracking-wide text-gray-600 uppercase">Scale Details</label>

      <div class="pb-2 mt-2 overflow-y-auto custom-scroll">
        <table class="w-full font-serif border">
          <tbody>
            <tr class="border-b">
              <th class="px-2 py-1 text-xs text-left uppercase whitespace-no-wrap border-r">Scale Degree</th>
              <template v-for="(note, index) in this.currentScale.formula.degree">
                <td :key="index" class="px-2 py-1 text-sm text-center">{{note}}</td>
                <td v-if="index + 1 < currentScale.formula.degree.length" :key="index + 'x'">&nbsp;</td>
              </template>
            </tr>
            <tr class="bg-gray-100 border-b">
              <th class="px-2 py-1 text-xs text-left uppercase whitespace-no-wrap border-r">Formula</th>
              <template v-for="(note, index) in this.currentScale.formula.names">
                <td :key="index" class="px-2 py-1 text-sm text-center ">{{note}}</td>
                <td v-if="index + 1 < currentScale.formula.degree.length" :key="index + 'x'">&nbsp;</td>
              </template>
            </tr>
            <tr class="border-b">
              <th class="px-2 py-1 text-xs text-left uppercase whitespace-no-wrap border-r">Note Name</th>
              <template v-for="(note, index) in [...this.currentScale.scale.notes(), this.currentScale.scale.notes()[0]]">
                <td :key="index" class="px-2 py-1 font-bold text-center ">{{formatChord(note.name().toUpperCase() + note.accidental())}}</td>
                <td v-if="index + 1 < currentScale.formula.degree.length" :key="index + 'x'">&nbsp;</td>
              </template>
            </tr>
            <tr class="bg-gray-100 border-b">
              <th class="px-2 py-1 text-xs text-left uppercase whitespace-no-wrap border-r">Degree Name</th>
              <template v-for="(note, index) in this.currentScale.formula.degree_name">
                <td :key="index" style="font-size: 10px;" class="px-2 py-1 text-sm text-center whitespace-no-wrap">{{note}}</td>
                <td v-if="index + 1 < currentScale.formula.degree.length" :key="index + 'x'">&nbsp;</td>
              </template>
            </tr>
            <tr class="border-b">
              <th class="px-2 py-1 text-xs text-left uppercase whitespace-no-wrap border-r">Interval (US)</th>
              <template v-for="(note, index) in this.currentScale.formula.interval_us">
                <td :key="index + 'x'">&nbsp;</td>
                <td :key="index" class="px-2 py-1 text-sm text-center ">{{note}}</td>
                <td v-if="currentScale.formula.interval_us.length  - 1 === index" :key="index + 'y'">&nbsp;</td>
              </template>
            </tr>
            <tr class="bg-gray-100">
              <th class="px-2 py-1 text-xs text-left uppercase whitespace-no-wrap border-r">Interval (UK)</th>
              <template v-for="(note, index) in this.currentScale.formula.interval_uk">
                <td :key="index + 'x'">&nbsp;</td>
                <td :key="index" class="px-2 py-1 text-sm text-center ">{{note}}</td>
                <td v-if="currentScale.formula.interval_uk.length  - 1 === index" :key="index + 'y'">&nbsp;</td>
              </template>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="mt-2 text-right">
      <a @click="showLegend=!showLegend" class="px-2 py-1 text-xs text-green-500 underline cursor-pointer hover:text-green-700">{{showLegend ? 'Hide' : 'Show'}} Legend</a>
    </div>

    <div v-show="showLegend" class="">
      <div class="px-2 pb-2 mt-2 mb-2 text-xs border-b grid grid-cols-2 md:grid-cols-4">
        <div class="mb-1"><span class="inline-block w-8 font-bold whitespace-no-wrap">R</span>Root note</div>
        <div class="mb-1"><span class="inline-block w-8 font-bold whitespace-no-wrap">m</span>Minor interval</div>
        <div class="mb-1"><span class="inline-block w-8 font-bold whitespace-no-wrap">M</span>Major interval</div>
        <div class="mb-1"><span class="inline-block w-8 font-bold whitespace-no-wrap">P</span>Perfect interval</div>
        <div class="mb-1"><span class="inline-block w-8 font-bold whitespace-no-wrap">W</span>Whole tone</div>
        <div class="mb-1"><span class="inline-block w-8 font-bold whitespace-no-wrap">H</span>Half tone</div>
        <div class="mb-1"><span class="inline-block w-8 font-bold whitespace-no-wrap">T</span>Tone</div>
        <div class="mb-1"><span class="inline-block w-8 font-bold whitespace-no-wrap">S</span>Semitone</div>
      </div>

      <div class="mt-2 overflow-y-auto text-xs custom-scroll">
        <table class="w-full">
          <tbody>
            <tr class="">
              <th class="pb-1 pl-2 text-left align-top">Tonic</th>
              <td class="pb-1 pl-2 pr-2">The tonic is the first note, or first degree, or a scale. It’s also commonly called the root, and serves as the tonal center of the key.</td>
            </tr>
            <tr class="">
              <th class="pb-1 pl-2 text-left align-top">Supertonic</th>
              <td class="pb-1 pl-2 pr-2">A whole step above the tonic, and has a predominant function.</td>
            </tr>
            <tr class="">
              <th class="pb-1 pl-2 text-left align-top">Mediant</th>
              <td class="pb-1 pl-2 pr-2">The mediant is the third degree of a scale and very importantly establishes whether the key is major or minor.</td>
            </tr>
            <tr class="">
              <th class="pb-1 pl-2 text-left align-top">Subdominant</th>
              <td class="pb-1 pl-2 pr-2">The Subdominant, or fourth degree, is the third strongest note of the scale. It is the weakest of the three primary notes: the Tonic, Subdominant, and Dominant.</td>
            </tr>
            <tr class="">
              <th class="pb-1 pl-2 text-left align-top">Dominant</th>
              <td class="pb-1 pl-2 pr-2">The dominant is a perfect fifth above the tonic it has a strong, commanding presence and holds a dominant role in establishing tonal tension and resolution within a musical composition.</td>
            </tr>
            <tr class="">
              <th class="pb-1 pl-2 text-left align-top">Submediant</th>
              <td class="pb-1 pl-2 pr-2">The submediant refers to the sixth scale degree in a diatonic scale. It is called the submediant because it is positioned just below the mediant, which is the third scale degree.</td>
            </tr>
            <tr class="">
              <th class="pb-1 pl-2 text-left align-top">Leading Tone or Subtonic</th>
              <td class="pb-1 pl-2 pr-2">The leading tone has a strong tendency to lead or resolve to the tonic. In certain scales or modes where the seventh note of the scale is a tone away from the tonic (Mixolydian/Natural Minor), it's called the Subtonic.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
  </Panel>
</template>

<script>
import Panel from "./Panel.vue";

export default {
  data() {
    return {
      showLegend: false
    }
  },
  props: {
    currentScale: Object,
    formatChord: Function,
  },
  components: {
    Panel
  }
}
</script>
